import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

const STORAGE_PREFIX = 'poeier:signup-popup';
const DISMISSED_KEY = `${STORAGE_PREFIX}:dismissed-at`;
const SUBSCRIBED_KEY = `${STORAGE_PREFIX}:subscribed`;
const SESSION_START_KEY = `${STORAGE_PREFIX}:session-start`;
const SESSION_SHOWN_KEY = `${STORAGE_PREFIX}:shown`;
const DAY_MS = 24 * 60 * 60 * 1000;
const IMAGE_WARMUP_MS = 2000;

/**
 * @param {Storage} storage
 * @param {string} key
 * @returns {string | null}
 */
function read(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * @param {Storage} storage
 * @param {string} key
 * @param {string} value
 */
function write(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode, blocked cookies) – the popup just won't remember.
  }
}

/**
 * Newsletter sign-up popup. Opens once per browser session after a delay (counted from the
 * session's first page view), and stays closed for good once dismissed or subscribed —
 * unless the "show again after" setting allows it to return.
 *
 * Attributes (rendered by snippets/signup-popup.liquid):
 * - data-delay: seconds before opening
 * - data-dismiss-days: days before a dismissed popup may show again; 0 = never
 * - data-show-mobile: present when the popup may open below 750px
 * - data-open-on-load: open immediately (form result or theme editor preview)
 * - data-subscribed: the visitor just subscribed successfully
 * - data-preview: theme editor preview – ignore and don't touch storage
 *
 * @extends DialogComponent
 */
class SignupPopupComponent extends DialogComponent {
  /** @type {number | undefined} */
  #timer;

  /** @type {number | undefined} */
  #warmupTimer;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose);

    if (this.dataset.subscribed !== undefined) {
      write(localStorage, SUBSCRIBED_KEY, '1');
    }

    if (this.dataset.openOnLoad !== undefined) {
      this.#open();
      return;
    }

    if (!this.#isEligible()) return;

    const now = Date.now();
    let sessionStart = Number(read(sessionStorage, SESSION_START_KEY));
    if (!sessionStart) {
      sessionStart = now;
      write(sessionStorage, SESSION_START_KEY, String(now));
    }

    const delay = Number(this.dataset.delay || 0) * 1000;
    const remaining = Math.max(0, sessionStart + delay - now);

    // The image is lazy, and lazy images inside a closed dialog don't load until it opens –
    // start the download shortly beforehand so it doesn't pop in.
    this.#warmupTimer = window.setTimeout(() => this.#loadImages(), Math.max(0, remaining - IMAGE_WARMUP_MS));

    this.#timer = window.setTimeout(() => {
      if (this.#isEligible()) this.#open();
    }, remaining);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.#timer);
    window.clearTimeout(this.#warmupTimer);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleClose);
  }

  #loadImages() {
    for (const image of this.querySelectorAll('img[loading="lazy"]')) {
      image.loading = 'eager';
    }
  }

  #open() {
    this.#loadImages();
    if (!this.#isPreview) write(sessionStorage, SESSION_SHOWN_KEY, '1');
    this.showDialog();
  }

  #handleClose = () => {
    if (this.#isPreview) return;
    write(localStorage, DISMISSED_KEY, String(Date.now()));
  };

  get #isPreview() {
    return this.dataset.preview !== undefined;
  }

  /**
   * @returns {boolean}
   */
  #isEligible() {
    if (read(localStorage, SUBSCRIBED_KEY)) return false;
    if (read(sessionStorage, SESSION_SHOWN_KEY)) return false;

    if (this.dataset.showMobile === undefined && window.matchMedia('(max-width: 749px)').matches) {
      return false;
    }

    const dismissedAt = Number(read(localStorage, DISMISSED_KEY));
    if (dismissedAt) {
      const days = Number(this.dataset.dismissDays || 0);
      if (days === 0) return false;
      if (Date.now() - dismissedAt < days * DAY_MS) return false;
    }

    return true;
  }
}

if (!customElements.get('signup-popup-component')) {
  customElements.define('signup-popup-component', SignupPopupComponent);
}
